import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  ShieldCheck,
  Search,
  ScanSearch,
  Mail,
  Phone,
  CreditCard,
  Globe,
  IdCard,
  Fingerprint,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

// Common PII column name patterns for auto-detection
const PII_PATTERNS = [
  /email/i,
  /e_mail/i,
  /phone/i,
  /mobile/i,
  /cell/i,
  /ssn/i,
  /social_security/i,
  /aadhaar/i,
  /aadhar/i,
  /passport/i,
  /credit_card/i,
  /card_number/i,
  /pan_number/i,
  /pan_no/i,
  /ip_address/i,
  /address/i,
  /street/i,
  /zip/i,
  /postal/i,
  /dob/i,
  /date_of_birth/i,
  /birth_date/i,
  /salary/i,
  /income/i,
  /bank_account/i,
  /account_number/i,
  /ifsc/i,
  /routing_number/i,
  /first_name/i,
  /last_name/i,
  /full_name/i,
  /customer_name/i,
  /employee_name/i,
  /patient_name/i,
];

const PII_TYPES = [
  { icon: Mail, label: "Email addresses", example: "user@example.com → [EMAIL_1]" },
  { icon: Phone, label: "Phone numbers", example: "+1 (555) 123-4567 → [PHONE_1]" },
  { icon: CreditCard, label: "Credit / debit card numbers", example: "4111 1111 1111 1111 → [CARD_1]" },
  { icon: IdCard, label: "Social Security Numbers (SSN)", example: "123-45-6789 → [SSN_1]" },
  { icon: Globe, label: "IP addresses", example: "192.168.1.1 → [IPV4_1]" },
  { icon: Fingerprint, label: "Aadhaar numbers", example: "1234 5678 9012 → [AADHAAR_1]" },
  { icon: IdCard, label: "Passport numbers", example: "AB1234567 → [PASSPORT_1]" },
];

interface PiiFieldRecord {
  id: string;
  client_id: string;
  table_name: string;
  column_name: string;
  is_pii: boolean;
}

const PiiMasking = () => {
  const { clientInfo, profile } = useAuth();
  const { toast } = useToast();
  const { schemaData, isLoading: schemaLoading, refetch: refetchSchema } = useMcpSchema();

  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // PII fields from DB
  const [piiFields, setPiiFields] = useState<PiiFieldRecord[]>([]);
  const [piiFieldsLoading, setPiiFieldsLoading] = useState(true);

  // Scanning state
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);

  // Search / filter
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());

  // Saving individual field toggle
  const [savingField, setSavingField] = useState<string | null>(null);

  // Load client PII masking setting
  useEffect(() => {
    if (!clientInfo) return;
    const loadSetting = async () => {
      const { data } = await supabase
        .from("client")
        .select("pii_masking_enabled")
        .eq("id", clientInfo.id)
        .single();
      setEnabled(data?.pii_masking_enabled ?? false);
      setLoading(false);
    };
    loadSetting();
  }, [clientInfo]);

  // Load existing PII field records
  useEffect(() => {
    if (!profile?.client_id) return;
    const loadPiiFields = async () => {
      const { data } = await (supabase
        .from("pii_fields" as any)
        .select("id, client_id, table_name, column_name, is_pii")
        .eq("client_id", profile.client_id) as any);
      setPiiFields((data as PiiFieldRecord[]) ?? []);
      setPiiFieldsLoading(false);
      if (data && data.length > 0) setScanned(true);
    };
    loadPiiFields();
  }, [profile?.client_id]);

  // Build a lookup map: "table.column" -> PiiFieldRecord
  const piiMap = useMemo(() => {
    const map = new Map<string, PiiFieldRecord>();
    for (const f of piiFields) {
      map.set(`${f.table_name}.${f.column_name}`, f);
    }
    return map;
  }, [piiFields]);

  // Handle global PII masking toggle
  const handleToggle = async (value: boolean) => {
    if (!clientInfo || saving) return;
    setSaving(true);
    const { error } = await supabase
      .from("client")
      .update({ pii_masking_enabled: value })
      .eq("id", clientInfo.id);
    setSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setEnabled(value);
      toast({
        title: value ? "PII masking enabled" : "PII masking disabled",
        description: value
          ? "Sensitive data will be masked before being sent to the AI."
          : "Messages will be sent to the AI without masking.",
      });
    }
  };

  // Scan schema: auto-detect PII fields and upsert to DB
  const handleScanSchema = async () => {
    if (!profile?.client_id || schemaData.length === 0) return;
    setScanning(true);

    try {
      // Refresh schema to get latest
      await refetchSchema();

      const rows: { client_id: string; table_name: string; column_name: string; is_pii: boolean }[] = [];

      for (const table of schemaData) {
        for (const col of table.columns) {
          const key = `${table.table_name}.${col.column_name}`;
          const existingRecord = piiMap.get(key);

          // If already in DB, keep existing value
          if (existingRecord) continue;

          // Auto-detect based on column name patterns
          const isPii = PII_PATTERNS.some((pat) => pat.test(col.column_name));

          rows.push({
            client_id: profile.client_id!,
            table_name: table.table_name,
            column_name: col.column_name,
            is_pii: isPii,
          });
        }
      }

      if (rows.length > 0) {
        // Batch upsert
        const { error } = await (supabase
          .from("pii_fields" as any)
          .upsert(rows as any, { onConflict: "client_id,table_name,column_name" }) as any);

        if (error) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        } else {
          // Reload PII fields
          const { data } = await (supabase
            .from("pii_fields" as any)
            .select("id, client_id, table_name, column_name, is_pii")
            .eq("client_id", profile.client_id) as any);
          setPiiFields((data as PiiFieldRecord[]) ?? []);
          setScanned(true);
          toast({
            title: "Schema scanned",
            description: `Found ${rows.length} new fields. ${rows.filter((r) => r.is_pii).length} auto-detected as PII.`,
          });
        }
      } else {
        setScanned(true);
        toast({ title: "Up to date", description: "All schema fields are already catalogued." });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }

    setScanning(false);
  };

  // Toggle a field's PII status
  const handleFieldToggle = async (tableName: string, columnName: string, newValue: boolean) => {
    if (!profile?.client_id) return;
    const key = `${tableName}.${columnName}`;
    setSavingField(key);

    const existing = piiMap.get(key);

    if (existing) {
      const { error } = await (supabase
        .from("pii_fields" as any)
        .update({ is_pii: newValue, updated_at: new Date().toISOString() } as any)
        .eq("id", existing.id) as any);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        setPiiFields((prev) =>
          prev.map((f) => (f.id === existing.id ? { ...f, is_pii: newValue } : f))
        );
      }
    } else {
      // Insert new record
      const { data, error } = await (supabase
        .from("pii_fields" as any)
        .insert({
          client_id: profile.client_id,
          table_name: tableName,
          column_name: columnName,
          is_pii: newValue,
        } as any)
        .select("id, client_id, table_name, column_name, is_pii")
        .single() as any);

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else if (data) {
        setPiiFields((prev) => [...prev, data as PiiFieldRecord]);
      }
    }

    setSavingField(null);
  };

  // Toggle table expanded/collapsed
  const toggleTable = (tableName: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  // Filtered schema data based on search
  const filteredSchema = useMemo(() => {
    if (!searchQuery.trim()) return schemaData;
    const q = searchQuery.toLowerCase();
    return schemaData
      .map((table) => ({
        ...table,
        columns: table.columns.filter(
          (col) =>
            col.column_name.toLowerCase().includes(q) ||
            table.table_name.toLowerCase().includes(q)
        ),
      }))
      .filter((table) => table.columns.length > 0);
  }, [schemaData, searchQuery]);

  // Count PII fields per table
  const piiCountByTable = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of piiFields) {
      if (f.is_pii) {
        counts[f.table_name] = (counts[f.table_name] || 0) + 1;
      }
    }
    return counts;
  }, [piiFields]);

  const totalPiiFields = piiFields.filter((f) => f.is_pii).length;

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="h-6 w-6" /> PII Masking
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Protect sensitive user data by masking Personally Identifiable Information before it
          reaches the AI model. Masked values are automatically restored in the AI response.
        </p>
      </div>

      {/* Toggle */}
      <div className="flex items-center gap-4 rounded-lg border border-border bg-card p-4 max-w-lg">
        <div className="flex-1">
          <Label htmlFor="pii-toggle" className="text-base font-medium">
            Enable PII masking
          </Label>
          <p className="text-sm text-muted-foreground mt-0.5">
            {enabled
              ? "Active — PII is replaced with placeholders before sending to the AI."
              : "Inactive — messages are sent to the AI as-is."}
          </p>
        </div>
        {saving ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <Switch
            id="pii-toggle"
            checked={enabled}
            onCheckedChange={handleToggle}
          />
        )}
      </div>

      {/* Schema Scan Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between max-w-4xl">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Schema PII Field Detection
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Scan your database schema to identify and mark fields that contain PII data.
              {totalPiiFields > 0 && (
                <span className="ml-1 font-medium text-foreground">
                  {totalPiiFields} field{totalPiiFields !== 1 ? "s" : ""} marked as PII.
                </span>
              )}
            </p>
          </div>
          <Button
            onClick={handleScanSchema}
            disabled={scanning || schemaLoading}
            className="gap-2"
          >
            {scanning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ScanSearch className="h-4 w-4" />
            )}
            {scanning ? "Scanning…" : scanned ? "Rescan Schema" : "Scan Schema"}
          </Button>
        </div>

        {/* Search field */}
        {scanned && schemaData.length > 0 && (
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tables or columns…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Schema field list */}
        {scanned && !piiFieldsLoading && filteredSchema.length > 0 && (
          <div className="max-w-4xl space-y-2">
            {filteredSchema.map((table) => {
              const isExpanded = expandedTables.has(table.table_name);
              const piiCount = piiCountByTable[table.table_name] || 0;

              return (
                <Card key={table.table_name} className="overflow-hidden">
                  <button
                    onClick={() => toggleTable(table.table_name)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-accent/50 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="text-sm font-medium font-mono">{table.table_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {table.columns.length} column{table.columns.length !== 1 ? "s" : ""}
                    </span>
                    {piiCount > 0 && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                        {piiCount} PII
                      </Badge>
                    )}
                  </button>

                  {isExpanded && (
                    <div className="border-t border-border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40%]">Column</TableHead>
                            <TableHead className="w-[30%]">Type</TableHead>
                            <TableHead className="w-[30%] text-center">PII Field</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {table.columns.map((col) => {
                            const key = `${table.table_name}.${col.column_name}`;
                            const record = piiMap.get(key);
                            const isPii = record?.is_pii ?? false;
                            const isSaving = savingField === key;

                            return (
                              <TableRow key={key}>
                                <TableCell className="font-mono text-sm">
                                  {col.column_name}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {col.data_type}
                                </TableCell>
                                <TableCell className="text-center">
                                  {isSaving ? (
                                    <Loader2 className="h-4 w-4 animate-spin mx-auto text-muted-foreground" />
                                  ) : (
                                    <Switch
                                      checked={isPii}
                                      onCheckedChange={(val) =>
                                        handleFieldToggle(table.table_name, col.column_name, val)
                                      }
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {scanned && !piiFieldsLoading && filteredSchema.length === 0 && searchQuery && (
          <p className="text-sm text-muted-foreground">No tables or columns match your search.</p>
        )}

        {!scanned && !scanning && (
          <Card className="flex flex-col items-center justify-center py-10 max-w-4xl">
            <ScanSearch className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Click "Scan Schema" to analyze your database and automatically identify fields that
              may contain PII. You can then review and adjust each field's classification.
            </p>
          </Card>
        )}
      </div>

      {/* How it works */}
      <div className="max-w-lg space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          How it works
        </h2>
        <ol className="space-y-2 text-sm text-foreground list-decimal list-inside">
          <li>User sends a message that may contain sensitive data.</li>
          <li>
            The system scans the message and replaces each PII value with an opaque
            placeholder (e.g. <code className="bg-muted rounded px-1">[EMAIL_1]</code>).
          </li>
          <li>The masked message is forwarded to the AI model.</li>
          <li>Tool calls generated by the AI are unmasked before executing database queries,
            so query results remain accurate.</li>
          <li>Placeholders in the AI's final response are replaced with the original values
            before the answer is shown to the user.</li>
          <li>
            Users without PII access (<code className="bg-muted rounded px-1">pii_enabled = false</code>)
            will see masked values for all fields marked as PII across chat, reports, and dashboards.
          </li>
        </ol>
      </div>

      {/* Detected PII types */}
      <div className="max-w-lg space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Auto-detected PII types
        </h2>
        <ul className="space-y-2">
          {PII_TYPES.map(({ icon: Icon, label, example }) => (
            <li
              key={label}
              className="flex items-start gap-3 rounded-md border border-border bg-card p-3"
            >
              <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground font-mono">{example}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PiiMasking;
